import { Disclosure } from '@headlessui/react'
import Translation from 'components/Translation'
import { SetStateAction, useEffect, useState } from 'react'
import { ServiceItem } from 'types/service'
import { classNames } from 'utils/dom-helpers'
import { ModalDataAttributes } from '../types'
import { InfoIcon } from 'utils/custom-icons'
import { getTranslation } from 'utils/language-helper'
import getConfig from 'next/config'

function SelectServices({
    attributes,
    setAttributes,
    translations,
    onCancel,
    onPrevious,
    onNext,
    displayFooter = true,
    mainHeading = 'membership_services_desc',
    isDirty = false,
    setIsDirty = () => false,
    type = 'SERVICE',
}: {
    attributes?: ModalDataAttributes
    setAttributes(r: ModalDataAttributes): void
    translations: Record<string, string>
    onCancel(): void
    onPrevious(): void
    onNext(): void
    displayFooter?: boolean
    mainHeading?: string
    isDirty?: boolean
    setIsDirty?: React.Dispatch<SetStateAction<boolean>>
    type?: string
}) {
    const [selected_categories, setSelectedCategories] = useState<string[]>(
        (attributes?.selected_categories as string[]) || []
    )
    const [selected_services, setSelectedServices] = useState<string[]>(
        (attributes?.selected_services as string[]) || []
    )
    const [services, setServices] = useState<ServiceItem[]>(
        (attributes?.services as unknown as ServiceItem[]) || []
    )

    const [checkbox_categories, setCheckboxCategories] = useState<string[]>(
        []
    )
    const [checkbox_services, setCheckboxServices] = useState<string[]>([])

    const [search, setSearch] = useState<string>('')
    const { NEXT_PUBLIC_STATIC_DOMAIN } = getConfig().publicRuntimeConfig

    // initialization
    let categories: Record<string, string>[] = []

    if (type === 'SERVICE') {
        categories = []
        services.forEach((svc: ServiceItem) => {
            if (
                categories.filter((item) => {
                    return svc.category?.id == item?.id
                }).length == 0
            ) {
                categories.push(svc.category)
            }
        })
    } else {
        categories = []
        let cat = attributes?.membership_categories as Record<
            string,
            string
        >[]
        const mapped = cat?.map((item) => ({
            id: item?.value,
            name: item?.text,
        }))

        // services.forEach((svc: any) => {
        //     if (
        //         mapped.filter((item) => {
        //             return svc.id == item?.id
        //         }).length == 0
        //     ) {
        //         categories.push([...mapped] as any)
        //     }
        // })
        categories = mapped
    }

    useEffect(() => {
        setServices(
            (attributes?.services as unknown as ServiceItem[]) || []
        )
    }, [attributes?.services])

    useEffect(() => {
        let updated_checkbox_categories: string[] = []
        let updated_checkbox_services: string[] = []
        selected_categories.forEach((id) => {
            updated_checkbox_categories.push(id)
            if (type === 'SERVICE') {
                services.forEach((svc) => {
                    if (svc?.category?.id == id) {
                        updated_checkbox_services.push(svc?.id)
                    }
                })
            } else {
                services.forEach((svc: any) => {
                    if (svc?.categoryId == id) {
                        updated_checkbox_services.push(svc?.id)
                    }
                })
            }
        })
        selected_services.forEach((id) => {
            updated_checkbox_services.push(id)
        })

        setCheckboxCategories(updated_checkbox_categories)
        setCheckboxServices(updated_checkbox_services)
    }, [attributes])

    function handleCategoryClick(id: string) {
        const updated_checkbox_categories = [...checkbox_categories]
        const updated_checkbox_services = [...checkbox_services]
        let idx = updated_checkbox_categories.indexOf(id)
        if (idx >= 0) {
            updated_checkbox_categories.splice(idx, 1)
            // uncheck all services
            services.forEach((svc: any) => {
                if (
                    type === 'SERVICE'
                        ? svc.category.id == id
                        : svc?.categoryId == id
                ) {
                    let service_idx = updated_checkbox_services.indexOf(
                        svc.id
                    )
                    if (service_idx >= 0) {
                        updated_checkbox_services.splice(service_idx, 1)
                    }
                }
            })
        } else {
            updated_checkbox_categories.push(id)
            // check all services
            services.forEach((svc: any) => {
                if (
                    type === 'SERVICE'
                        ? svc.category.id == id
                        : svc?.categoryId == id
                ) {
                    let service_idx = updated_checkbox_services.indexOf(
                        svc.id
                    )
                    if (service_idx == -1) {
                        updated_checkbox_services.push(svc.id)
                    }
                }
            })
        }
        setSelectedAttributes(
            updated_checkbox_categories,
            updated_checkbox_services
        )
    }

    function handleServiceClick(service: any) {
        const updated_checkbox_categories = [...checkbox_categories]
        const updated_checkbox_services = [...checkbox_services]
        let idx = updated_checkbox_services.indexOf(service.id)
        if (idx >= 0) {
            updated_checkbox_services.splice(idx, 1)
            let cat_idx = updated_checkbox_categories.indexOf(
                type === 'SERVICE'
                    ? service.category.id
                    : service?.categoryId
            )
            if (cat_idx >= 0) {
                updated_checkbox_categories.splice(cat_idx, 1)
            }
        } else {
            // Check if all services are checked
            updated_checkbox_services.push(service.id)
            let svcs = services.filter((svc: any) => {
                return type === 'SERVICE'
                    ? svc.category.id == service.category.id
                    : svc.categoryId === service?.categoryId
            })
            if (
                svcs.every((svc) => {
                    return updated_checkbox_services.indexOf(svc.id) >= 0
                })
            ) {
                updated_checkbox_categories.push(service.category?.id || service?.categoryId)
            }
        }
        setSelectedAttributes(
            updated_checkbox_categories,
            updated_checkbox_services
        )
    }

    function setSelectedAttributes(
        updated_checkbox_categories: string[],
        updated_checkbox_services: string[]
    ) {
        setCheckboxCategories(updated_checkbox_categories)
        setCheckboxServices(updated_checkbox_services)

        let selected_categories: string[] = updated_checkbox_categories
        let selected_services: string[] = []
        services.forEach((svc: any) => {
            if (
                updated_checkbox_categories.indexOf(
                    type === 'SERVICE'
                        ? svc.category.id
                        : (svc.categoryId as string)
                ) == -1 &&
                updated_checkbox_services.indexOf(svc.id) >= 0
            ) {
                selected_services.push(svc.id)
            }
        })

        setSelectedCategories(selected_categories)
        setSelectedServices(selected_services)
        setAttributes({
            ...attributes,
            selected_categories: selected_categories,
            selected_services: selected_services,
        })
        setIsDirty(true)
    }

    return (
        <div className="membership-title mb-5">
           <div>
           <Translation
                render_as="h3"
               
                content_key="membership_services_title"
            />
            <Translation
                render_as="p"
            
                content_key={translations[mainHeading]}
            />
           </div>
            <div className="grid grid-cols-6 p-3 rounded-lg bg-gray-50 my-6 w-full">
                <div className="col-span-2 border-r p-3 overflow-y-auto h-80">
                    <div className="text-primary text-lg font-medium font-display mb-2">
                        {attributes?.name}
                    </div>
                    <div className="border-b pb-2">
                        <span className="pr-2 border-r border-gray-400">
                            <i className="feather-package mr-1" />{' '}
                            {selected_categories?.length}{' '}
                            <Translation content_key="categories" />
                        </span>
                        <span className="pl-2">
                            <i className="feather-briefcase mr-1" />{' '}
                            {selected_services?.length}
                            {''}
                            <Translation content_key="individual_services" />
                        </span>
                    </div>
                    {selected_categories?.length == 0 &&
                        selected_services?.length == 0 && (
                            <div className="flex flex-col text-gray-400 text-center h-80 px-8">
                                <img
                                    className="h-40 w-40 mx-auto my-5"
                                    src={`https://${NEXT_PUBLIC_STATIC_DOMAIN}/images/no_selected_services.svg`}
                                    alt="No Items Selected"
                                />
                                <Translation
                                    render_as="p"
                                    className="text-xl"
                                    content_key="no_selected_service_title"
                                />
                                <Translation
                                    render_as="p"
                                    content_key="no_selected_service_subtitle"
                                />
                            </div>
                        )}
                    {categories?.length > 0 &&
                        categories?.map((item: any, idx: number) => {
                            if (
                                selected_categories.indexOf(item?.id) >= 0
                            ) {
                                return (
                                    <div
                                        key={idx}
                                        className="flex justify-between py-2 pr-5"
                                    >
                                        <div className="flex items-center">
                                            <i className="feather-package text-xl mr-2 text-primary-light" />
                                            {item?.name}
                                        </div>
                                        <button
                                            className="float-right"
                                            onClick={() => {
                                                handleCategoryClick(
                                                    item?.id
                                                )
                                            }}
                                        >
                                            <i className="feather-x text-gray-400" />
                                        </button>
                                    </div>
                                )
                            }
                        })}
                    {services.map((service: ServiceItem, idx: number) => {
                        if (selected_services.indexOf(service.id) >= 0) {
                            return (
                                <div
                                    key={idx}
                                    className="flex justify-between py-2 pr-5"
                                >
                                    <div className="flex items-center">
                                        <i className="feather-briefcase text-xl mr-2" />
                                        {service.name}
                                    </div>
                                    <button
                                        className="float-right"
                                        onClick={() => {
                                            handleServiceClick(service)
                                        }}
                                    >
                                        <i className="feather-x text-gray-400" />
                                    </button>
                                </div>
                            )
                        }
                    })}
                </div>
                <div className="col-span-4 p-3">
                    <div className="relative ml-2">
                        <div className="absolute inset-y-0 right-2 flex items-center pointer-events-none">
                            <span className="feather feather-search text-gray-500 text-lg" />
                        </div>
                        <input
                            type="text"
                            name="keyword"
                            id="keyword"
                            className={classNames(
                                'focus:ring-primary-dark focus:border-primary-dark rounded-md',
                                'block w-full py-2 pl-9',
                                'border-gray-300 text-gray-500 placeholder-gray-300'
                            )}
                            onChange={(e) => {
                                setSearch(e.target.value.toLowerCase())
                            }}
                            placeholder={
                                translations['search_service_category']
                            }
                        />
                    </div>
                    <div className="flex flex-col overflow-y-auto h-80">
                        {categories?.map((item: any) => {
                            return (
                                <div
                                    key={item?.id as string}
                                    className="border-b"
                                >
                                    <Disclosure defaultOpen>
                                        {({ open }) => (
                                            <>
                                                <div className="flex gap-3 text-primary text-sm py-2 w-full text-left justify-between items-center">
                                                    <div className="flex items-center gap-2 text-left">
                                                        <input
                                                            type="checkbox"
                                                            className="h-6 w-6 mx-2 border-gray-300 rounded text-primary focus:ring-primary-light checked:bg-primary-light"
                                                            checked={
                                                                checkbox_categories.indexOf(
                                                                    item?.id
                                                                ) >= 0
                                                            }
                                                            onChange={() => {
                                                                handleCategoryClick(
                                                                    item?.id
                                                                )
                                                            }}
                                                        />
                                                        <label className="font-semibold">
                                                            {item?.name}
                                                        </label>
                                                        <span
                                                            title={getTranslation(
                                                                'category_helper_text',
                                                                translations
                                                            )}
                                                        >
                                                            <InfoIcon />
                                                        </span>
                                                        {/* <i className="feather-info text-gray-400 ml-2 mr-1" />
                                                        <Translation
                                                            render_as="span"
                                                            className="text-sm text-gray-400"
                                                            content_key="category_helper_text"
                                                        /> */}
                                                    </div>
                                                    <Disclosure.Button>
                                                        <i
                                                            className={classNames(
                                                                'text-xl',
                                                                open
                                                                    ? 'feather-chevron-down'
                                                                    : 'feather-chevron-right'
                                                            )}
                                                        />
                                                    </Disclosure.Button>
                                                </div>
                                                <Disclosure.Panel className="grid grid-cols-2 overflow-hidden rounded-b-lg">
                                                    {services
                                                        .filter(
                                                            (s) =>
                                                                s.name
                                                                    .toLowerCase()
                                                                    .indexOf(
                                                                        search
                                                                    ) > -1
                                                        )
                                                        .map(
                                                            (
                                                                service: any,
                                                                idx: number
                                                            ) => {
                                                                if (
                                                                    type ===
                                                                    'SERVICE'
                                                                        ? (service
                                                                              ?.category
                                                                              ?.id as string) ==
                                                                          item?.id
                                                                        : service?.categoryId ===
                                                                          item?.id
                                                                ) {
                                                                    return (
                                                                        <div
                                                                            key={
                                                                                idx
                                                                            }
                                                                            className="flex items-center text-sm py-2 gap-1"
                                                                        >
                                                                            <input
                                                                                type="checkbox"
                                                                                className="h-6 w-6 mx-2 border-gray-300 rounded text-primary focus:ring-primary-light"
                                                                                checked={
                                                                                    checkbox_services.indexOf(
                                                                                        service.id
                                                                                    ) >=
                                                                                    0
                                                                                }
                                                                                onChange={() => {
                                                                                    handleServiceClick(
                                                                                        service
                                                                                    )
                                                                                }}
                                                                            />
                                                                            <span className="font-medium">
                                                                                {
                                                                                    service.name
                                                                                }
                                                                            </span>
                                                                        </div>
                                                                    )
                                                                } else
                                                                    return ''
                                                            }
                                                        )}
                                                </Disclosure.Panel>
                                            </>
                                        )}
                                    </Disclosure>
                                </div>
                            )
                        })}
                    </div>
                </div>
            </div>

            {displayFooter && (
                <>
                    <div className="pt-5 flex justify-end gap-3">
                        <button
                            type="button"
                            className="border border-gray-300 rounded-lg py-1 inline-block btn-gap"
                            onClick={() => {
                                onCancel()
                            }}
                        >
                            <Translation
                                content_key="cancel"
                                translations={translations}
                            />
                        </button>
                        <div className="flex justify-end gap-3">
                            <button
                                type="button"
                                className="border border-gray-300 rounded-lg py-1 inline-block btn-gap"
                                onClick={() => {
                                    onPrevious()
                                }}
                            >
                                <Translation
                                    content_key="previous"
                                    translations={translations}
                                />
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    onNext()
                                }}
                                className={classNames(
                                    'group relative flex justify-center',
                                    'btn-gap border border-transparent',
                                    'rounded-md text-white',
                                    'focus:outline-none',
                                    attributes?.loading
                                        ? 'bg-primary-light'
                                        : attributes?.api_error
                                        ? 'bg-red-700 hover:bg-primary-dark focus:ring-2 focus:ring-offset-2 focus:ring-primary-light'
                                        : 'bg-primary hover:bg-primary-dark focus:ring-2 focus:ring-offset-2 focus:ring-primary-light'
                                )}
                            >
                                <Translation
                                    content_key="next"
                                    translations={translations}
                                />
                            </button>
                        </div>
                    </div>
                </>
            )}
        </div>
    )
}

export default SelectServices
